import React, { FC } from 'react';
import ImagesDocuments from '@components/search/images';
import { isPhone, searchDisplayMode, sortByField } from '@helpers';
import { DOCUMENT_ENTITY_TYPE, INDIRECT_LINKING_TEXT } from '@components/imageHandling/common';
import ColorWrapper, { WithColor } from '@styles/colors';
import { SCREEN_PADDING_HORIZONTAL, TABLE_NAMES, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { ScrollView, Text, View } from 'react-native';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import i18n from 'i18n-js';
import ImagesDocumentsGallery from '@v2/pages/nc-file/details/modules/nc-file/components/displayModes/ImagesDocumentsGallery';
import { SmallLoading } from '@components/loading';
import Svg from '@styles/icons/svg';
import { ReferencesObject } from '@v2/components/imageHandling/types/types';
import { useManageImagesDocumentsContent } from './hooks/useManageImagesDocumentsContent';

export interface ImagesDocumentsContentProps extends WithColor {
  documentRetrieveKey: (typeof DOCUMENT_ENTITY_TYPE)[keyof typeof DOCUMENT_ENTITY_TYPE];
  documentRetrieveId: number;
  bigSliderBg?: string;
  smallSliderBg?: string;
}

const ImagesDocumentsContent: FC<ImagesDocumentsContentProps> = ({
  documentRetrieveId,
  documentRetrieveKey,
  bigSliderBg,
  smallSliderBg,
  C,
}) => {
  const {
    displayMode,
    generateImageURL,
    totalCount,
    galleryContainerMeasurements,
    setGalleryContainerMeasurements,
    mainContainerMeasurements,
    setMainContainerMeasurements,
    apiRef,
    columnVisibilityModel,
    setColumnVisibilityModel,
    filter,
    setFilter,
    filterModel,
    setFilterModel,
    orderBy,
    setOrderBy,
    sortModel,
    setSortModel,
    oDataFilter,
    setODataFilter,
    imagesDocuments,
    selectedIndex,
    setSelectedIndex,
    loading,
    error,
    loadImagesDocuments,
    cropImageDocument,
    removeImageDocument,
    rotateImageDocument,
    isTableMode,
    isGalleryMode,
    hasValidMeasurements,
  } = useManageImagesDocumentsContent({ documentRetrieveKey, documentRetrieveId });

  if (!displayMode) return null;

  const renderContent = () => {
    if (!loading && imagesDocuments.length === 0 && hasValidMeasurements) {
      return (
        <View
          testID={TEST_IDS.ncFileDocuments.empty}
          style={[
            { flex: 1, maxWidth: '100%', justifyContent: 'center', alignContent: 'center', alignItems: 'center' },
            isPhone && { marginTop: scale(100) },
          ]}
        >
          <Svg icon="search" color="Label/Light/Tertiary" width={scale(45)} height={scale(45)} />
          <Text style={[font['Title 2'], { color: C('Label/Light/Primary'), textAlign: 'center', marginTop: scale(5) }]}>{i18n.t('No Results')}</Text>
        </View>
      );
    }

    if (isGalleryMode) {
      return (
        <View
          testID={TEST_IDS.ncFileDocuments.galleryContainer}
          style={{ flex: 1, width: '100%', height: '100%' }}
          onLayout={({
            nativeEvent: {
              layout: { width, height },
            },
          }) => setGalleryContainerMeasurements({ width, height })}
        >
          {!hasValidMeasurements || loading ? (
            <View style={{ marginTop: scale(40) }}>
              <SmallLoading />
            </View>
          ) : (
            <ImagesDocumentsGallery
              {...{
                cropImage: cropImageDocument,
                rotateImage: rotateImageDocument,
                deleteImage: removeImageDocument,
                C,
                containerHeight: galleryContainerMeasurements.height,
                containerWidth: galleryContainerMeasurements.width,
                selectedIndex,
                setSelectedIndex: setSelectedIndex,
                bigSliderBg,
                smallSliderBg,
              }}
            />
          )}
        </View>
      );
    }

    if (!isTableMode && imagesDocuments) {
      const documentsObject: ReferencesObject = imagesDocuments.reduce((acc, document) => {
        const scopeName = document.scopeName as keyof ReferencesObject;
        if (!scopeName) return acc;
        if (!acc[scopeName]) {
          acc[scopeName] = [document];
        } else {
          acc[scopeName].push(document);
        }
        return acc;
      }, {} as ReferencesObject);

      const firstElementIndexWithDocuments = Object.values(documentsObject).findIndex((documents) => documents.length > 0);

      return (
        <ScrollView showsVerticalScrollIndicator={false}>
          {Object.entries(documentsObject)
            .sort(([keyA], [keyB]) => {
              return keyA === INDIRECT_LINKING_TEXT ? 1 : keyB === INDIRECT_LINKING_TEXT ? -1 : 0;
            })
            .map(([scopeName, documents], index) => {
              if (!documents.length) return null;

              return (
                <View key={scopeName} style={{ gap: scale(18) }}>
                  <Text style={[font['Title 2'], { color: C('Label/Light/Primary'), marginLeft: scale(SCREEN_PADDING_HORIZONTAL) }]}>
                    {i18n.t(scopeName)}
                  </Text>
                  <ImagesDocuments
                    {...{
                      withoutLoadingIndicator: index !== firstElementIndexWithDocuments,
                      withoutVerticalCentered: true,
                      containerWidth: mainContainerMeasurements.width,
                      totalResultCount: documents.length,
                      searchResult: documents,
                      isError: !!error,
                      loaded: !loading,
                      C,
                      isSmallMode: displayMode === searchDisplayMode.square,
                      isLineMode: displayMode === searchDisplayMode.line,
                      isInfoTilesMode: displayMode === searchDisplayMode.infoTiles,
                      isTableMode: displayMode === searchDisplayMode.tableCompact || displayMode === searchDisplayMode.tableStandard,
                      displayMode,
                      fetchData: loadImagesDocuments,
                      generateImageURL,
                      recentlyViewed: [],
                      setRecentlyViewed: () => {},
                      facets: null,
                      tableName: TABLE_NAMES.detailsImagesDocuments,
                    }}
                  />
                </View>
              );
            })}
        </ScrollView>
      );
    }

    const sortFilterData = () => {
      if (!sortModel) return imagesDocuments;
      const { field, sort: sortDirection } = sortModel?.[0] ?? {};
      if (!field || !sortDirection) return imagesDocuments;

      return sortByField({ items: imagesDocuments, field, sortDirection, locale: i18n.locale });
    };

    const sortedFilteredDocumentForTable = sortFilterData();

    return (
      <ImagesDocuments
        {...{
          withoutVerticalCentered: true,
          containerWidth: galleryContainerMeasurements.width - SCREEN_PADDING_HORIZONTAL * 2,
          withoutHorizontalPaddings: true,
          totalResultCount: totalCount,
          searchResult: sortedFilteredDocumentForTable,
          isError: !!error,
          loaded: !loading,
          C,
          search: () => {},
          isSmallMode: displayMode === searchDisplayMode.square,
          isLineMode: displayMode === searchDisplayMode.line,
          isInfoTilesMode: displayMode === searchDisplayMode.infoTiles,
          isTableMode,
          displayMode,
          density: displayMode || 'standard',
          fetchData: loadImagesDocuments,
          generateImageURL,
          recentlyViewed: [],
          setRecentlyViewed: () => {},
          facets: null,
          apiRef,
          setOrderBy,
          orderBy,
          sortModel,
          setSortModel,
          columnVisibilityModel,
          setColumnVisibilityModel,
          filter,
          setFilter,
          filterModel,
          setFilterModel,
          oDataFilter,
          setODataFilter,
          tableName: TABLE_NAMES.detailsImagesDocuments,
        }}
      />
    );
  };

  return (
    <View
      onLayout={({
        nativeEvent: {
          layout: { width, height },
        },
      }) => setMainContainerMeasurements({ width, height })}
      style={{
        flex: 1,
        width: '100%',
        height: '100%',
        position: 'relative',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {renderContent()}
    </View>
  );
};

const ImagesDocumentsContentWrapped = ColorWrapper(ImagesDocumentsContent);

export { ImagesDocumentsContentWrapped as ImagesDocumentsContent };
